import { ChevronDownIcon, PinFilledIcon } from '@village/icons';
import { Button, Text } from '@village/ui';
import { Image } from 'components/image';
import { rem } from 'polished';
import styled from 'styled-components';
import { device } from 'utils/device';

const Container = styled.div`
    padding: ${rem(32)} 0;
    border-bottom: 2px solid ${({ theme }) => theme.vui.component.tag.default.backgroundColor};
`;

const Wrapper = styled.div`
    @media ${device.minLaptopM} {
        display: grid;
        grid-template-columns: 1fr 1fr;
    }
`;

const PrnNotice = styled.div`
    display: flex;
    margin: ${rem(4)} 0;
`;

const ProviderContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ProviderDetails = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: ${rem(16)};
    align-items: flex-start;

    @media ${device.maxLaptopM} {
        margin-bottom: 0;
    }
`;

const ProviderImage = styled(Image)`
    border-radius: 50%;
    object-fit: cover;
    margin-right: ${rem(24)};
    cursor: pointer;
    height: ${rem(90)};
    width: ${rem(90)};

    @media ${device.maxTablet} {
        height: 2.75rem;
        width: 2.75rem;
    }
`;

const ProviderName = styled(Text)`
    text-decoration: none;
    color: ${({ theme }) => theme.vui.colors.text.grayDefault};

    &:hover {
        color: ${({ theme, ...props }) => ('href' in props && props.href ? theme.vui.colors.action.primary.default : undefined)};
    }
`;

const ProviderSpecialty = styled(Text)`
    margin-bottom: ${rem(8)};
    color: ${({ theme }) => theme.vui.colors.text.grayDefault};

    @media ${device.maxTablet} {
        margin-bottom: 0;
    }
`;

const DepartmentContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${rem(4)};
    & svg {
        padding-top: 3px;
        box-sizing: content-box;
    }
`;

const Department = styled(Text)`
    display: block;
    padding: 0;
    margin: 0 0 0.5rem;
    color: ${({ theme }) => theme.vui.colors.text.grayDefault};
`;

const Bio = styled.div<{ $expanded: boolean }>`
    display: -webkit-box;
    -webkit-line-clamp: ${({ $expanded }) => ($expanded ? 'none' : '3')};
    -webkit-box-orient: vertical;
    overflow: hidden;
    color: ${({ theme }) => theme.vui.colors.text.grayMuted};

    @media ${device.maxLaptopM} {
        display: none;
    }

    p {
        margin-bottom: 0.5rem;
        line-height: 1.5rem;
    }
`;

const ExpandToggle = styled.div`
    margin-top: 0.5rem;
    > a {
        text-decoration: underline;
    }

    @media ${device.maxLaptopM} {
        display: none;
    }
`;

const NoAppointmentsContainer = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    row-gap: 1rem;
    padding: 0 3rem;

    @media ${device.maxLaptopM} {
        padding: 0;
        margin-top: ${rem(16)};
    }
`;

const AppointmentsContainer = styled.div`
    display: flex;
    width: 100%;
    flex: 1;
    flex-direction: column;

    @media ${device.maxLaptopM} {
        margin-top: 1rem;
    }

    @media ${device.maxTablet} {
        overflow-x: auto;
        -ms-overflow-style: none;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }
`;

const AppointmentsGroup = styled.div`
    display: flex;
    width: 100%;
    padding-left: ${rem(64)};
    padding-right: ${rem(48)};

    @media ${device.maxTablet} {
        padding-left: 0;
        padding-right: 0;
    }
`;

const AppointmentsColumn = styled.div`
    text-align: center;
    width: 100%;
    min-height: 2.25rem;
    flex: 1;
`;

const AppointmentList = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, 6rem);
    grid-gap: 0.5rem;

    @media ${device.minTablet} {
        text-align: center;
        display: flex;
        align-items: center;
        flex: 1;
        flex-direction: column;
    }
`;

const SlotPlaceholder = styled.span`
    display: flex;
    width: auto;
    height: 2.25rem;
    text-align: center;
    color: ${({ theme }) => theme.vui.colors.text.disabled};
    align-items: center;
    justify-content: center;

    @media ${device.maxTablet} {
        margin-right: 0.5rem;

        &:last-of-type {
            margin: 0;
        }
    }
`;

const SlotButton = styled(Button)`
    white-space: nowrap;

    @media ${device.minTablet} {
        padding: 0;
        width: 4.75rem;
        height: 2.25rem;
    }

    @media ${device.maxTablet} {
        &:last-of-type {
            margin: 0;
        }
    }
`;

const AvailabilityControllerWrapper = styled.div`
    margin: 1.25rem auto 0;
    width: 100%;
    display: flex;
    justify-content: center;
`;

const ViewAllAvailabilityIcon = styled(ChevronDownIcon).attrs(() => ({ size: 'xs' }))`
    margin-left: 0.25rem;
`;

const ViewLessAvailabilityIcon = styled(ViewAllAvailabilityIcon)`
    margin-left: 0.25rem;
    transform: rotate(180deg);
    transition: transform 1s ease-in-out;
`;

const PinIcon = styled(PinFilledIcon)`
    @media ${device.maxLaptopM} {
        display: none;
    }
`;

const RefWrapper = styled.div`
    scroll-margin-top: ${rem(200)};
`;

export {
    AppointmentList,
    AppointmentsColumn,
    AppointmentsContainer,
    AppointmentsGroup,
    AvailabilityControllerWrapper,
    Bio,
    Container,
    Department,
    DepartmentContainer,
    ExpandToggle,
    NoAppointmentsContainer,
    PinIcon,
    PrnNotice,
    ProviderContainer,
    ProviderDetails,
    ProviderImage,
    ProviderName,
    ProviderSpecialty,
    RefWrapper,
    SlotPlaceholder,
    SlotButton,
    ViewAllAvailabilityIcon,
    ViewLessAvailabilityIcon,
    Wrapper,
};
